import {Component, Inject, OnInit} from '@angular/core';
import {BaseCustomDialog} from '../base-custom-dialog';
import {DateTimeHelper, TimePeriodType} from '../../../../helper/date-time.helper';
import { IActionTypes } from '../../../../models/action.model';
import {ActionIconHelper} from '../../../../helper/action-icon.helper';
import { ICompanyContacts, IContact, ProfileStudio } from '../../../../models/leads/leads.model';
import {UserTokenService} from '../../../../services/user-service/user-token.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ACTION_DATA_SERVICE, ActionDataService} from '../../../../services/action/data.service';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../../services/leads/leads-data.service';
import {AppointmentsHelper} from '../../../../helper/appointments.helper';
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../../../../services/profile/profile-data.service';
import { LAST_SELECTED_STUDIO_ID } from '../../../../constants/constants';

@Component({
  selector: 'fi-negotiation-create',
  templateUrl: './negotiation-create.component.html',
  styleUrls: ['./negotiation-create.component.scss'],
  standalone: false,
})
export class NegotiationCreateComponent extends BaseCustomDialog implements OnInit {
  contactName = '';
  leadId: number;
  selectedDateOptionId: number;
  appointmentTypeId: number;
  showroomAppointment: IActionTypes;
  showroomTypeId: string;

  readonly dateOptionTypes = DateTimeHelper.datePeriodTypes;

  appointmentOptionTypes: IActionTypes[] = [];
  showroomTypes: ProfileStudio[] = [];
  searchedText = '';
  note = '';
  selectedDate: Date;
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('NEGOTIATION_CREATE', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('NEGOTIATION_CREATE', true);
  submitting: boolean;
  companyContacts: ICompanyContacts[];
  contactToDisplay: IContact;
  appointmentExists = false;
  inValidNote = false;
  isExternal = false;

  constructor(
    private userTokenService: UserTokenService,
    public dialogRef: MatDialogRef<NegotiationCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: { contactName: string, leadId: number, title: string },
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(PROFILE_DATA_SERVICE) private profileDataService: ProfileDataService,
  ) {
    super(dialogRef);

    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.title = dialogData?.title;

    actionDataService.getAppointmentTypes().subscribe(({success, data}) => {
      if (success) {
        this.appointmentOptionTypes = data.filter(item => item.IsLocation);
        this.showroomAppointment = this.appointmentOptionTypes.find(item => item.Name === 'SCHAURAUM');
      }
    });

    this.profileDataService.getStudios().subscribe(( { success, data } ) => {
      if ( success ) {
        this.showroomTypes = data;
        if (!this.isExternal) {
          this.showroomTypeId = sessionStorage.getItem(LAST_SELECTED_STUDIO_ID) ?? '';
        }
      }
    });

    this.leadsDataService.getLeadById(this.leadId).subscribe(({success, data}) => {
      if (success) {
        this.companyContacts = data.CompanyContacts;
        this.contactToDisplay = data.Contact;
      }
    });
  }

  onDateSelect(date: Date, time = false): void {
    this.selectedDateOptionId = null;

    if (!date) {
      this.selectedDate = null;
      return;
    }

    if (!this.selectedDate) {
      this.selectedDate = date;
    } else if (!time) {
      this.selectedDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    } else {
      this.selectedDate.setHours(date.getHours(), date.getMinutes());
    }

    this.selectedDate = new Date(this.selectedDate);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  setPeriods($event): void {
    this.selectedDateOptionId = $event;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(this.dateOptionTypes.find(item => item.Id === +$event));
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  onDateOptionSelected(type: TimePeriodType): void {
    this.selectedDateOptionId = type.Id;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(type);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  async ngOnInit(): Promise<void> {
    const userId = await this.userTokenService.getId();
    AppointmentsHelper.getDbAppointments(this.actionDataService, userId);
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  get formIsValid(): boolean {
    const hasRequiredFields = !!this.leadId && !!this.selectedDate && !!this.appointmentTypeId;
    // checks if selected appointment is showroom making that field mandatory
    const isShowroomRequired = !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ? !!this.showroomTypeId : true;
    const isNotSubmitting = !this.submitting;
    const isNoteValid = !this.inValidNote;

    return hasRequiredFields && isShowroomRequired && isNotSubmitting && isNoteValid;
  }

  showRoomText = ( item: ProfileStudio ) => `${item.KKZ} - ${item.Name} - ${item.City}`;

  inValidMaxLength(): boolean {
    this.inValidNote = this.note.length > 2000;
    return this.inValidNote;
  }

  submit(): void {

    if (this.formIsValid) {
      this.submitting = true;
      this.actionDataService.createNegotiation(
        this.leadId,
        this.note,
        this.appointmentTypeId,
        this.selectedDate,
        !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ? this.showroomTypeId : undefined,
      ).subscribe(({success, data}) => {
        if (success) {
          this.clearClassAndCloseDialog(this.dialogRef, 'remove');
        }

        if (!this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id) {
          sessionStorage.setItem(LAST_SELECTED_STUDIO_ID, this.showroomTypeId);
        }

        this.submitting = false;
      });
    }
  }
}
